import { useThreads } from '@copilotkit/react-core';
import { Message } from '@copilotkit/shared';
import { useAgent } from '@copilotkitnext/react';
import { ThumbDislikeRegular, ThumbLikeRegular } from '@fluentui/react-icons';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { feedbackService } from 'src/dataServices/FeedbackService';
import localeService from 'src/dataServices/LocaleService';
import { supportTelemetryService } from 'src/dataServices/SupportTelemetryService';
import { ChatHistoryItem } from 'src/state/contactus/types';
import { RootState } from 'src/state/reduxStore';

type FeedbackPolarity = 'positive' | 'negative';

interface IFeedbackModalState {
  isOpen: boolean;
  polarity: FeedbackPolarity;
  responseId: string;
}

interface IFeedbackContextValue {
  feedbackLoopEnabled: boolean;
  thumbsUpIcon?: React.ReactNode;
  thumbsDownIcon?: React.ReactNode;
  onThumbsUp?: (message: Message) => void;
  onThumbsDown?: (message: Message) => void;
  feedbackState: IFeedbackModalState;
  threadId: string;
  chatHistory: ChatHistoryItem[];
  submitFeedback: (
    reasonCode: string,
    reasonCodeLabel: string | undefined,
    reasonCodeDescription: string | undefined,
    comment: string
  ) => Promise<void>;
  closeFeedback: () => void;
}

const CLOSED_FEEDBACK_STATE: IFeedbackModalState = { isOpen: false, polarity: 'positive', responseId: '' };

export const FeedbackContext = React.createContext<IFeedbackContextValue>({
  feedbackLoopEnabled: false,
  feedbackState: CLOSED_FEEDBACK_STATE,
  threadId: '',
  chatHistory: [],
  submitFeedback: async () => {},
  closeFeedback: () => {}
});

export const FeedbackContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { threadId } = useThreads();
  const { agent } = useAgent({ agentId: 'support_agent' });
  const messages = agent?.messages ?? [];

  const feedbackLoopEnabled = useSelector((state: RootState) => state.flags.feedbackLoopEnabled);

  const [feedbackState, setFeedbackState] = React.useState<IFeedbackModalState>(CLOSED_FEEDBACK_STATE);

  React.useEffect(() => {
    if (!feedbackLoopEnabled) {
      setFeedbackState(CLOSED_FEEDBACK_STATE);
    }
  }, [feedbackLoopEnabled]);

  const handleThumbsUp = (message: Message) => {
    supportTelemetryService.trackFeedbackClicked(message.id, 'positive', threadId);
    setFeedbackState({ isOpen: true, polarity: 'positive', responseId: message.id });
  };

  const handleThumbsDown = (message: Message) => {
    supportTelemetryService.trackFeedbackClicked(message.id, 'negative', threadId);
    setFeedbackState({ isOpen: true, polarity: 'negative', responseId: message.id });
  };

  const submitFeedback = async (
    reasonCode: string,
    reasonCodeLabel: string | undefined,
    reasonCodeDescription: string | undefined,
    comment: string
  ) => {
    await feedbackService.submitFeedback({
      threadId,
      responseId: feedbackState.responseId,
      polarity: feedbackState.polarity,
      reasonCode,
      reasonCodeLabel,
      reasonCodeDescription,
      comment
    });
  };

  const closeFeedback = () => {
    setFeedbackState((prev) => ({ ...prev, isOpen: false }));
  };

  const chatHistory: ChatHistoryItem[] = messages
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .slice(-5)
    .map((m) => ({
      role: m.role,
      content: typeof m.content === 'string' ? m.content : ''
    }));

  const value: IFeedbackContextValue = {
    feedbackLoopEnabled,
    thumbsUpIcon: feedbackLoopEnabled ? (
      <ThumbLikeRegular title={localeService.getText('ContactUs', 'FeedbackThumbsUp')} />
    ) : undefined,
    thumbsDownIcon: feedbackLoopEnabled ? (
      <ThumbDislikeRegular title={localeService.getText('ContactUs', 'FeedbackThumbsDown')} />
    ) : undefined,
    onThumbsUp: feedbackLoopEnabled ? handleThumbsUp : undefined,
    onThumbsDown: feedbackLoopEnabled ? handleThumbsDown : undefined,
    feedbackState,
    threadId,
    chatHistory,
    submitFeedback,
    closeFeedback
  };

  return <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>;
};

export const useFeedbackContext = () => React.useContext(FeedbackContext);
